// components/BioLinksPage.jsx
import React, { useEffect, useState, useRef } from "react";
import "./BioLinksPage.css";
import { client } from "../lib/sanity"; // Adjust path as needed
import "../Components/Preloader.css"; // Import the CSS for the animated text
import { Helmet } from "react-helmet";
import { ProtectedEmail, ProtectedPhone, INAQ_CONTACTS } from "../utils/ContactObfuscator";

const BioLinksPage = () => {
  const [latestPosts, setLatestPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [todayTip, setTodayTip] = useState(null);
  const b2bContainerRef = useRef(null);

  useEffect(() => {
    // Animation timing constant
    const INITIAL_DELAY = 700;

    // Reveal the B2B
    setTimeout(() => {
      if (b2bContainerRef.current) {
        b2bContainerRef.current.classList.add("show-b2b");
      }
    }, INITIAL_DELAY);

    // Fetch latest blog posts from Sanity
    client
      .fetch(
        `*[_type == "post"] | order(publishedAt desc)[0...4] {
          _id,
          title,
          excerpt,
          slug,
          mainImage {
            asset->{
              _id,
              url
            }
          },
          publishedAt,
          "categories": categories[]->title
        }`
      )
      .then((data) => {
        setLatestPosts(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching blog posts:", error);
        setIsLoading(false);
      });

    // Fetch today's daily tip (separate fetch, fail silently — section just won't render)
    fetch("/api/sanity/daily-tips/today")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data._id) setTodayTip(data);
      })
      .catch(() => {});
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { day: "numeric", month: "long", year: "numeric" };
    return date.toLocaleDateString("sk-SK", options);
  };

  return (
    <div className='bio-links-page'>
      <Helmet>
        {/* Fonts are inlined in public/index.html - no external requests needed */}
      </Helmet>
      {/* Background container with gradient and grain */}
      <div className='background-container'>
        {/* Gradient background */}
        <div
          className='gradient-background'
          style={{
            background:
              "radial-gradient(ellipse at top left, #241025 20%, transparent 70%), radial-gradient(circle at bottom right, #1a0b1c 30%, #09010a 100%)",
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        />
        {/* Grain overlay */}
        <div className='grain-overlay'></div>
      </div>
      {/* Content Container */}
      <div className='content-container'>
        <div className='profile-section'>
          <div className='profile-image-container'>
            <div className='profile-image-wrapper'>
              <svg
                id='IQ_logo'
                data-name='IQ logo'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='20 0 930 869'
              >
                <defs>
                  <style>
                    {`.cls-1 {
                      fill: #241025;
                    }`}
                  </style>
                </defs>
                <circle
                  className='cls-1'
                  cx='432.68'
                  cy='406.63'
                  r='46.86'
                  transform='translate(-38 768.59) rotate(-80.78)'
                />
                <path
                  className='cls-1'
                  d='M479.54,520.07v138.23c0,.48-.01.95-.03,1.42-.74,25.22-21.43,45.44-46.83,45.44s-46.09-20.22-46.83-45.44c-.02-.47-.03-.94-.03-1.42v-138.23c0-25.88,20.98-46.86,46.86-46.86s46.86,20.98,46.86,46.86Z'
                />
                <path
                  className='cls-1'
                  d='M590.1,507.34c.55,14.01,15.99,25.25,34.95,25.25s34.4-11.24,34.95-25.25c0-.26.02-.52.02-.79v-63.26c-.19-19.29-.38-36.95-.57-56.25,0-5.97-3.84-12.55-11.63-17.98-5.52-3.85-11.66-4.89-11.66-4.89-.04-.01-.08-.01-.12-.02-6.65-1.28-12.18-2.1-17.32-2.61h-63.7c-.28,0-.56,0-.84-.02h-.16c-12.87-.53-23.13-11.12-23.13-24.11v-52.96c0-13.33,11.2-21.91,24.52-21.91h17.48c13.11,0,23.74,10.63,23.74,23.74v14.76c0,11.65,9.45,21.1,21.1,21.1h19.08c12.82,0,23.21-10.39,23.21-23.21v-16.59c0-6.87-.56-11.52-2.22-17.87-8.8-33.7-39.77-54.63-76.23-54.63h-40.31c-43.33,0-78.46,35.13-78.46,78.46v53.67c19.47,6.11,35.71,25.67,38.2,47.37l.15,1.6c1.46,11.01,13.69,19.55,25.06,19.79l7.32-.09c44.8.19,56.25-3.61,56.45,14.19l.12,82.51Z'
                />
              </svg>
            </div>
          </div>

          {/* Replace static h2 with animated text elements */}
          <div className='text-container' style={{ margin: "15px 0" }}>
            <span className='marketingova'>Marketingová</span>
            <div ref={b2bContainerRef} className='b2b-container'>
              <span className='b2b'>B2B</span>
            </div>
            <span className='agentura'>agentúra</span>
          </div>

          <p className='profile-description'>
            Získajte náskok s našim prístupom, know-how a marketingom.
          </p>
        </div>
        <a
          href='https://ai.inaq.sk'
          target='_blank'
          rel='noopener noreferrer'
          className='bio-link-item blog-post-item'
        >
          <div className='link-image-container'>
            <img
              src='/images/ai-offer-bio.jpg'
              alt='Naša AI ponuka'
              className='link-image'
            />
          </div>
          <div className='link-content'>
            <span className='link-title'>Naša AI ponuka</span>
            <div className='link-icon'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 24 24'
                fill='currentColor'
                width='24'
                height='24'
              >
                <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
              </svg>
            </div>
          </div>
        </a>
        {todayTip && (
          <a
            href='/bio/marketing-ai-tipy'
            className='bio-link-item blog-post-item daily-tip-card'
            data-testid='today-tip-card'
          >
            <div className='link-image-container'>
              {todayTip.mainImage ? (
                <img
                  src={todayTip.mainImage}
                  alt={`Dnešný tip #${todayTip.tipNumber}: ${todayTip.title}`}
                  className='link-image'
                />
              ) : (
                <div
                  className='link-image'
                  style={{
                    background:
                      "linear-gradient(135deg, #241025 0%, #5a2a4f 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 48,
                    fontWeight: 700,
                  }}
                >
                  #{todayTip.tipNumber}
                </div>
              )}
            </div>
            <div className='link-content'>
              <span
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  color: "#ff6b9d",
                  marginBottom: 4,
                  display: "block",
                  fontWeight: 600,
                }}
              >
                Dnešný tip #{todayTip.tipNumber}
              </span>
              <span className='link-title'>{todayTip.title}</span>
              <div className='link-icon'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                  width='24'
                  height='24'
                >
                  <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                </svg>
              </div>
            </div>
          </a>
        )}
        <div className='section-header'>
          <h3>Novinky z blogu</h3>
        </div>
        {/* Blog Posts Section */}
        <div className='links-section blog-posts-section'>
          {isLoading
            ? // Loading placeholders
              Array(4)
                .fill()
                .map((_, index) => (
                  <div
                    key={`loader-${index}`}
                    className='bio-link-item skeleton'
                  >
                    <div className='link-image-container skeleton-img'></div>
                    <div className='link-content'>
                      <span className='link-title skeleton-text'></span>
                      <div className='link-date skeleton-text-small'></div>
                    </div>
                  </div>
                ))
            : // Actual blog posts
              latestPosts.map((post) => (
                <a
                  key={post._id}
                  href={`/blog/${post.slug?.current || ""}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='bio-link-item blog-post-item'
                >
                  <div className='link-image-container'>
                    <img
                      src={
                        post.mainImage?.asset.url || "/images/default-blog.png"
                      }
                      alt={post.title}
                      className='link-image'
                    />
                  </div>
                  <div className='link-content'>
                    <span className='link-title'>{post.title}</span>
                    <div className='link-icon'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 24 24'
                        fill='currentColor'
                        width='24'
                        height='24'
                      >
                        <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                      </svg>
                    </div>
                  </div>
                </a>
              ))}
        </div>
        {/* Original content section */}
        <div className='section-header'>
          <h3>Špecializujeme sa na</h3>
        </div>
        <div className='bio-links-grid'>
          {/* Top row - 2 cards */}
          <div className='row'>
            <div
              className='bio-card'
              onClick={() =>
                (window.location.href = "/marketing-pre-stavebnictvo")
              }
            >
              <div
                className='card-image'
                style={{
                  backgroundImage:
                    'url("https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/stavebnefirmy/marketing_stavebne_spolocnosti_b2b_agentura.jpg")',
                }}
              >
                <div className='black-overlay'></div>
                <div className='gradient-overlay'>
                  <h3>
                    Stavebníctvo
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                      width='18'
                      height='18'
                    >
                      <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                    </svg>
                  </h3>
                </div>
              </div>
            </div>
            <div
              className='bio-card'
              onClick={() => (window.location.href = "/")}
            >
              <div
                className='card-image'
                style={{
                  backgroundImage:
                    'url("https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/otc_doplnky/marketing_b2b_otc_lieky_doplnky_vyzivy_sk.jpg")',
                }}
              >
                <div className='black-overlay'></div>
                <div className='gradient-overlay'>
                  <h3>
                    OTC doplnky
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                      width='18'
                      height='18'
                    >
                      <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                    </svg>
                  </h3>
                </div>
              </div>
            </div>
          </div>
          {/* Middle row - 1 card */}
          <div className='row'>
            <div
              className='bio-card wide-card'
              onClick={() => (window.location.href = "/")}
            >
              <div
                className='card-image'
                style={{
                  backgroundImage:
                    'url("https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/b2b_distribucne_spolocnosti/marketing_b2b_distribucne_spolocnosti_bio.jpg")',
                }}
              >
                <div className='black-overlay'></div>
                <div className='gradient-overlay'>
                  <h3>
                    Veľkoobchod a distribučné spoločnosti
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                      width='18'
                      height='18'
                    >
                      <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                    </svg>
                  </h3>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom row - 2 cards */}
          <div className='row'>
            <div
              className='bio-card'
              onClick={() => (window.location.href = "/marketing-pre-podlahy")}
            >
              <div
                className='card-image'
                style={{
                  backgroundImage:
                    'url("https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/podlahy/marketingova-agentura-podlahove-studio-predajna-s-podlahami-iq.avif")',
                }}
              >
                <div className='black-overlay'></div>
                <div className='gradient-overlay'>
                  <h3>
                    Podlahové štúdiá
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                      width='18'
                      height='18'
                    >
                      <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                    </svg>
                  </h3>
                </div>
              </div>
            </div>
            <div
              className='bio-card'
              onClick={() => (window.location.href = "/")}
            >
              <div
                className='card-image'
                style={{
                  backgroundImage:
                    'url("https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/market_entry_strategy/marketing_b2b_slovak_market_entry_strategy.avif")',
                }}
              >
                <div className='black-overlay'></div>
                <div className='gradient-overlay'>
                  <h3>
                    Market entry SK/CZ
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                      width='18'
                      height='18'
                    >
                      <path d='M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z' />
                    </svg>
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='footer-wrapper'>
        <div className='footer-container'>
          <div className='section-header'>
            <h3>Kontaktujte nás</h3>
          </div>
          <div className='footer-content'>
            <p className='footer-text'>
              Máte dnes marketing nastavený tak, že neprichádzate o zákazníkov?
              Ozvite sa nám a spolu pozrieme na to, ako vám vieme pomôcť.
            </p>

            {/* Website button - direct link */}
            <a
              href='http://www.inaq.sk'
              target='_blank'
              rel='noopener noreferrer'
              className='contact-button'
            >
              <span className='button-label'>web</span>
            </a>

            {/* Email button - protected */}
            <ProtectedEmail
              email={INAQ_CONTACTS.MARKETING_EMAIL}
              className='contact-button'
            >
              <span className='button-label'>e-mail</span>
            </ProtectedEmail>

            {/* Call button - protected and fixed typo */}
            <ProtectedPhone
              phone={INAQ_CONTACTS.PHONE_TEL}
              display={INAQ_CONTACTS.PHONE_DISPLAY}
              className='call-button'
            />

            {/* Copyright footer */}
            <div className='copyright-footer'>
              <p className='copyright-text'>
                © 2026 INAQ, s.r.o. Všetky práva vyhradené
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioLinksPage;
